import React from "react";

import { EditIcon, POIIcon, TrashIcon } from "@/assets/icons";
import CustomFilterArea from "@/components/dataset/datasetAttributeFilter/CustomFilterArea";
import { Button } from "@/components/ui/button";
import { MESSAGE } from "@/constants/message";
import { useAppFeatures } from "@/hooks/useAppFeatures";

import { twMerge } from "tailwind-merge";

interface AdminAreaCardProps {
  onClick: () => void;
  onDelete?: () => void;
  onSwitchChange?: (checked: boolean) => void;
  checked?: boolean;
  isGlobalFilterActive?: boolean;
  selectedCount?: number;
}

export const AdminAreaCard: React.FC<AdminAreaCardProps> = ({
  onClick,
  onDelete,
  onSwitchChange,
  checked = false,
  isGlobalFilterActive = false,
  selectedCount = 0
}) => {
  const { isGlobalFilterOverrideEnabled } = useAppFeatures();
  const hasData = selectedCount > 0;
  const isDisabledAdminArea = isGlobalFilterActive && !checked;

  return (
    <div className="bg-white-smoke p-4 rounded-xl flex flex-col">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-semibold text-txtHeading">
            Set Admin Area
          </h3>
          <p className="text-xs text-dim-gray leading-5">
            {MESSAGE.DATASET_ADMIN_AREA_POI_DESCRIPTION}
          </p>
        </div>
        {isGlobalFilterOverrideEnabled && (
          <CustomFilterArea
            disabled={!isGlobalFilterActive}
            checked={checked}
            onSwitchChange={(e) => onSwitchChange?.(e)}
          />
        )}
      </div>

      {hasData ? (
        <div
          className={twMerge(
            "flex items-center justify-between bg-white rounded-xl border border-gainsboro px-3 py-2",
            isDisabledAdminArea &&
              "opacity-50 cursor-not-allowed text-light-gray"
          )}
        >
          <div className="flex flex-col gap-0.5">
            <span
              className={twMerge(
                "text-xs font-semibold text-txtHeading",
                isDisabledAdminArea && "text-light-gray"
              )}
            >
              Adm. Area
            </span>
            <span
              className={twMerge(
                "text-[10px] text-dim-gray",
                isDisabledAdminArea && "text-light-gray"
              )}
            >
              Total adm area selected{" "}
              <span
                className={twMerge(
                  "text-txtHeading font-semibold",
                  isDisabledAdminArea && "text-light-gray"
                )}
              >
                ({selectedCount})
              </span>
            </span>
          </div>
          <div className="flex items-start gap-2 h-full">
            <button
              type="button"
              disabled={isDisabledAdminArea}
              aria-label="Delete admin area selection"
              className="w-5 h-5 flex items-center justify-center bg-transparent border-0 p-0 cursor-pointer group"
              onClick={onDelete}
            >
              <TrashIcon
                className={twMerge(
                  "w-4 h-4 fill-dim-gray group-hover:fill-red-500",
                  isDisabledAdminArea &&
                    "fill-light-gray group-hover:fill-light-gray"
                )}
              />
            </button>
            <button
              type="button"
              disabled={isDisabledAdminArea}
              aria-label="Edit admin area selection"
              className="w-5 h-5 flex items-center justify-center bg-transparent border-0 p-0 cursor-pointer"
              onClick={onClick}
            >
              <EditIcon
                className={twMerge(
                  "w-4 h-4 fill-txtHeading",
                  isDisabledAdminArea && "fill-light-gray"
                )}
              />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-1 py-2 mb-3">
          <POIIcon className="w-8 h-8 fill-light-gray" />
          <span className="text-xs text-dim-gray">Set admin area</span>
        </div>
      )}

      {!hasData && (
        <Button
          variant="outline"
          className="w-full rounded-xl h-8 text-teal-500 border-teal-500 font-semibold text-sm"
          onClick={onClick}
        >
          Adm. Area
        </Button>
      )}
    </div>
  );
};
